import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Alert, { ALERT_TYPES } from '../../components/atoms/Alert';
import Badge, { BadgeVariants } from '../../components/atoms/Badge';
import WarningMessage from '../../components/utilities/WarningMessage';
import { Label } from '../../components/widgets';
import AddFutureItems from '../containers/AddFutureItems';
import { CUSTOM_SOURCE_NAME, getTranslatedItemType, translationPrefix } from '../utils';
import css from './SourcePageStep.css';
import SearchableMultiSelect from './SearchableMultiSelect';
import StepCard from './StepCard';

const iconProps = {
    size: IconSizes.X_SMALL,
    variant: IconVariants.LIGHT,
    interactive: false,
};

const mapSourceItem = (source, membersPerSource, isApsalarEnabled) => {
    const extras = [];
    const inUseBy = membersPerSource[source.name] || [];

    if (source.disabled) {
        extras.push(
            <div key="disabled" className={css.extra}>
                <Icon name="ban" {...iconProps} />
                <Translate id={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.SAN_PARTNER_NOT_SUPPORTED`} />
            </div>
        );
    } else if (source.isSanPartner) {
        extras.push(
            <div key="sanPartner" className={css.extra}>
                <Icon name="info-circle" {...iconProps} />
                <Translate id={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.SAN_PARTNER`} />
            </div>
        );
    } else if (inUseBy.length > 0) {
        const displayNamesCount = 2;
        let names = inUseBy.slice(0, displayNamesCount).join(', ');
        if (inUseBy.length > displayNamesCount) {
            names += `, +${inUseBy.length - displayNamesCount}`;
        }
        extras.push(
            <div key="inUseBy" className={css.extra}>
                <Icon name="group-3-line" {...iconProps} />
                <Translate id={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.IN_USE_BY`} data={{ names }} />
            </div>
        );
    }

    if (source.name === CUSTOM_SOURCE_NAME) {
        const customInfoKey = isApsalarEnabled
            ? `${translationPrefix}.MANAGE_PERMISSIONS_PAGE.CUSTOM_SOURCE_INFO`
            : `${translationPrefix}.MANAGE_PERMISSIONS_PAGE.CUSTOM_SOURCE_INFO_ANALYTICS_ONLY`;
        extras.push(
            <div key="customInfo" className={css.extra}>
                <Icon name="info-circle" {...iconProps} />
                <Translate id={customInfoKey} />
            </div>
        );
    }

    return {
        ...source,
        extra: extras.length > 0 ? extras : null,
        tag: source.isSanPartner ? (
            <Badge variant={BadgeVariants.WARNING} color="var(--warning1Color)" className={css.viewOnlyTag}>
                <Icon name="eye" {...iconProps} variant={IconVariants.WARNING} />
                <Translate id={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.VIEW_ONLY`} />
            </Badge>
        ) : null,
        ...(source.icon && {
            Icon: 'img',
            iconProps: { src: source.icon, alt: source.name },
        }),
    };
};

const SourcePageStep = ({
    sources = [],
    topSourceNames = [],
    membersPerSource = {},
    onChange = () => {},
    onAllChange = () => {},
    enableAddFuture = () => {},
    onStepValidationStatusChanged = () => {},
    translate = () => {},
    removedSources = [],
    activeMemberType = '',
    isApsalarEnabled = false,
    showSanInfo = false,
    loading = false,
}) => {
    const selectedSourcesCount = sources.filter(source => source.selected).length;
    const disabledSourcesCount = sources.filter(source => source.disabled).length;
    const totalAvailableSourcesCount = sources.length;
    const hasRemovedSources = removedSources.length > 0;

    useEffect(() => {
        const isValid = selectedSourcesCount > 0;
        onStepValidationStatusChanged({
            isValid,
            title: isValid
                ? ''
                : translate('STATIC.PAGES.TEAM_MEMBERS.SELECT_AT_LEAST_ONE_TOOLTIP', {
                      itemType: getTranslatedItemType(translate, 'source'),
                  }),
        });
    }, [onStepValidationStatusChanged, translate, selectedSourcesCount]);

    const selectAll = value => {
        onAllChange(
            sources.filter(source => !source.disabled).map(source => source.id),
            value
        );
        if (value) {
            enableAddFuture();
        }
    };

    const onItemSelected = (selectedId, value) => {
        onChange(selectedId, value);
    };

    const sourceItems = useMemo(
        () => sources.map(source => mapSourceItem(source, membersPerSource, isApsalarEnabled)),
        [sources, membersPerSource, isApsalarEnabled]
    );

    return (
        <div className={css.container}>
            {hasRemovedSources && (
                <Alert
                    type={ALERT_TYPES.WARNING}
                    text={translate(`${translationPrefix}.SOURCE_REMOVAL_WARNING`)}
                    className={css.alert}
                    alertIconClassName={css.alertIcon}
                />
            )}
            <StepCard
                className={css.stepCard}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.SOURCES`}
                titleTextReplace={{
                    count: selectedSourcesCount,
                    total: totalAvailableSourcesCount - disabledSourcesCount,
                }}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.${activeMemberType}.SOURCES`}
            >
                {showSanInfo && (
                    <WarningMessage
                        type="info"
                        collapsible
                        collapsibleTitle={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.SAN_INFO_TITLE`}
                        message={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.SAN_INFO_MESSAGE`}
                        defaultExpanded
                        showIcon={false}
                    />
                )}
                {loading ? (
                    <div className={css.loadingState}>
                        <Label type="p" text={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.LOADING_SOURCES`} />
                    </div>
                ) : (
                    <SearchableMultiSelect
                        className={css.multiSelectContainer}
                        allItems={sourceItems}
                        filteredItems={sourceItems}
                        topItemIds={topSourceNames}
                        onChange={onItemSelected}
                        selectAll={selectAll}
                        itemType="sources"
                        searchPlaceholder={translate(`${translationPrefix}.PLACEHOLDER.SOURCE_SEARCH`)}
                    />
                )}
            </StepCard>
            {!loading && (
                <AddFutureItems itemType="source" form="base_source" dimensionFilter className={css.addFutureItems} />
            )}
        </div>
    );
};

const sourceShape = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    icon: PropTypes.string,
    selected: PropTypes.bool.isRequired,
    isSanPartner: PropTypes.bool,
});

SourcePageStep.propTypes = {
    sources: PropTypes.arrayOf(sourceShape),
    topSourceNames: PropTypes.arrayOf(PropTypes.string),
    membersPerSource: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    onChange: PropTypes.func,
    onAllChange: PropTypes.func,
    enableAddFuture: PropTypes.func,
    translate: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
    removedSources: PropTypes.arrayOf(PropTypes.string),
    activeMemberType: PropTypes.string,
    isApsalarEnabled: PropTypes.bool,
    showSanInfo: PropTypes.bool,
    loading: PropTypes.bool,
};

export default SourcePageStep;
